import { findCustomRendererKey, getCustomisationFieldData, getGroupData, updateCustomisationValue } from '../EditablePreview/EditablePreview';
import DimensionsModule from './modules/dimensions/DimensionsModule';
import './Modules.scss';
import PositionModule from "./modules/position/PositionModule";
import AppearanceModule from './modules/appearance/AppearanceModule';
import BorderModule from './modules/border/BorderModule';
import FillModule from './modules/fill/FillModule';
import TypographyModule from './modules/typography/TypographyModule';
import PaddingModule from './modules/padding/PaddingModule';
import CopyModule from './modules/copy/CopyModule';
import AnimationModule from './modules/animation/AnimationModule';
import CustomModule from './modules/custom/CustomModule';
import { Fragment, useEffect, useRef, useState } from 'react';
import ShadowModule from './modules/shadow/ShadowModule';
import AudioModule from './modules/audio/AudioModule';
import { WORKSPACE_LEFT_LIST_ICONS } from '../../WorkspaceAssets/Assets';
import PresetsModule from './PresetsModule';
import DisruptorModule from './modules/disruptor/DisruptorModule';
import { findDynamicNodeName } from '../../CustomisationWorkspaceMain';
import { slideOutPanelControl } from '../../../../../containers/TimelineEditor/slideout-panel/SlideoutPanel';
import { toast } from 'react-toastify';
import { removeVal, triggerSaveFeatureData } from '../../v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions';
import { IconJsxer } from '../../../../../helpers/IconHelper';
import { WORKING_ON_NODE_DATA } from '../EditablePreview/VisualisedComponents/VisualisedNode';
import { CampaignContext } from '../../../../../contexts/CampaignContext';
import { useContext } from 'react';

const getFieldLinkTail = (fieldLink) => {
    if (typeof fieldLink !== 'string' || !fieldLink.length) return '';
    return fieldLink.split('.').slice(-2).join('.');
};

const matchesSelectedFieldLink = (selectedFieldLink, candidateFieldLink) => {
    if (typeof selectedFieldLink !== 'string' || typeof candidateFieldLink !== 'string') return false;
    if (selectedFieldLink === candidateFieldLink) return true;
    const selectedTail = getFieldLinkTail(selectedFieldLink);
    const candidateTail = getFieldLinkTail(candidateFieldLink);
    if (selectedTail && candidateTail && selectedTail === candidateTail) return true;
    const selectedLast = selectedFieldLink.split('.').pop();
    const candidateLast = candidateFieldLink.split('.').pop();
    return !!selectedLast && selectedLast === candidateLast;
};

const resolveAddMultiplesLabel = ({ audioCustomisationVars, disruptorCustomisationVars, activeNodeName }) => {
    if (audioCustomisationVars) {
        return 'Add Sound';
    }

    if (disruptorCustomisationVars) {
        return 'Add Disruptor';
    }

    const normalizedActiveNodeName = typeof activeNodeName === 'string' ? activeNodeName.trim().toLowerCase() : '';
    if (normalizedActiveNodeName === 'sound board') {
        return 'Add Sound';
    }

    return `Add ${activeNodeName}`;
};

export const changeNumericalFieldValue = (customisationVars, fieldConfig, value, isInt = true, index = 0, forceComponentKey = null, node = null, isFinalValue = true, updateEvenIfValuesDontChange = false, forcePrevValueTo = null) => {
    // console.log('Changing numerical field value:', fieldConfig, value, isInt, index, forceComponentKey, node);
    if (fieldConfig?.dataLink) {
        const newValue = isInt ? parseInt(value, 10) : parseFloat(value);
        if (!isNaN(newValue)) {
            updateCustomisationValue(customisationVars, fieldConfig.dataLink, newValue, index, forceComponentKey, node, null, null, isFinalValue, false, isFinalValue, updateEvenIfValuesDontChange, forcePrevValueTo);
            return true;
        }
    }
    console.warn(`Invalid value for field ${fieldConfig?.dataLink}: ${value}`);
    return false;
}

export const changeStringFieldValue = (customisationVars, fieldConfig, value, index = 0, forceComponentKey = null, node = null, isFinalValue = true, updateEvenIfValuesDontChange = false, forcePrevValueTo = null) => {
    const linkString = typeof fieldConfig === 'string' ? fieldConfig : fieldConfig?.dataLink;
    if (linkString) {
        updateCustomisationValue(customisationVars, linkString, value, index, forceComponentKey, node, null, null, isFinalValue, false, isFinalValue, updateEvenIfValuesDontChange, forcePrevValueTo);
        return true;
    }
    console.warn(`Invalid value for field ${linkString}: ${value}`);
    return false;
}

export const changeValue_noType = (customisationVars, fieldConfig, value, index = 0, forceComponentKey = null, node = null, isFinalValue = true, updateEvenIfValuesDontChange = false, forcePrevValueTo = null) => {
    const linkString = typeof fieldConfig === 'string' ? fieldConfig : fieldConfig?.dataLink;
    if (linkString) {
        updateCustomisationValue(customisationVars, linkString, value, index, forceComponentKey, node, null, null, isFinalValue, false, isFinalValue, updateEvenIfValuesDontChange, forcePrevValueTo);
        return true;
    }
    console.warn(`Invalid value for field ${linkString}: ${value}`);
    return false;
}

const ModuleList = (props) => {
    const { groupConfig, customisationVars, activeNode, selectedFeatureNode, addVal } = props;
    const moduleListRef = useRef(null);

    const [campaignContext, setCampaignContext] = useContext(CampaignContext);

    const clearDirectFieldSelection = () => {
        if (!moduleListRef.current) return;
        moduleListRef.current.querySelectorAll('.v2-direct-selected-field').forEach((element) => {
            element.classList.remove('v2-direct-selected-field');
        });
        moduleListRef.current.querySelectorAll('.v2-direct-selected-image-control').forEach((element) => {
            element.classList.remove('v2-direct-selected-image-control');
        });
        moduleListRef.current.querySelectorAll('.v2-direct-selected-image-preview').forEach((element) => {
            element.classList.remove('v2-direct-selected-image-preview');
        });
    };
    
    // Hooks must be called before any early returns
    const [forceUpdateInc, setForceUpdateInc] = useState(0);
    const forceModuleListUpdate = () => setForceUpdateInc(forceUpdateInc + 1);
    
    const groupChildren = groupConfig?.children || [];
    const visualisationComponentKey = groupConfig?.visualisationConfig?.visualisationComponent !== '<Standard>' ? groupConfig?.visualisationConfig?.visualisationComponent : null;
    const activeNodeDataIndex = props.activeNode?.dataIndex ?? null;
    const groupSchema = getCustomisationFieldData(props.campaignData, props.activeNode?.featureData?._id, groupConfig?.groupSettings?.dataLink);
    const groupsValsArray = getGroupData(customisationVars, groupConfig?.groupSettings?.dataLink, -1);
    
    useEffect(() => {
        forceModuleListUpdate();
    }, [groupsValsArray?.length, props.forceUpdateModuleList]);

    useEffect(() => {
        clearDirectFieldSelection();
        if (!props.selectedFieldDataLink || !moduleListRef.current) return;

        const target = Array.from(moduleListRef.current.querySelectorAll('[data-field-link]')).find(
            (element) => matchesSelectedFieldLink(props.selectedFieldDataLink, element.getAttribute('data-field-link') || '')
        );

        if (target) {
            target.classList.add('v2-direct-selected-field');
            target.querySelectorAll('.v2-module-input-holder').forEach((element) => {
                element.classList.add('v2-direct-selected-image-control');
            });
            target.querySelectorAll('.image-preview-holder').forEach((element) => {
                element.classList.add('v2-direct-selected-image-preview');
            });
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        return () => {
            clearDirectFieldSelection();
        };
    }, [props.selectedFieldDataLink, props.activeNode?.id, props.activeNode?.path, forceUpdateInc]);
    
    // If nothing is selected, skip rendering entirely to avoid churn
    if (!activeNode && !selectedFeatureNode) {
        return null;
    }

    const nodesToList = [];
    let isListingMultiples = false;
    let listNode = activeNode ?? selectedFeatureNode;
    if (groupSchema?.allowMultiple && activeNodeDataIndex === null) {
        isListingMultiples = true;
        nodesToList.push(...props.activeNode.subNodes);
        nodesToList.pop();
    } else {
        if (groupSchema?.allowMultiple) {
            listNode = listNode?.parent ?? listNode;
        }
        nodesToList.push(props.activeNode);
    }

    let audioGroupDataLinkArray = props.activeTimelineAudioGroup ? props.activeTimelineAudioGroup?.audioData?.['audio-file']?.length ? props.activeTimelineAudioGroup.audioData['audio-file'].split('.') : null : null;
    if (audioGroupDataLinkArray === null) {
        // Maybe this is a simple toggle?
        audioGroupDataLinkArray = props.activeTimelineAudioGroup ? props.activeTimelineAudioGroup.audioData?.['audio-enabled']?.length ? props.activeTimelineAudioGroup.audioData['audio-enabled'].split('.') : null : null;
    }
    const audioGroupDataLink = audioGroupDataLinkArray ? audioGroupDataLinkArray.slice(0, -1).join('.') : null;
    const audioCustomisationVars = props.activeTimelineAudioGroup ? getGroupData(customisationVars, audioGroupDataLink, -1) : null;
    const audioGroupSchema = getCustomisationFieldData(props.campaignData, props.selectedFeatureNode?.featureData?._id, audioGroupDataLink, -1);
    const allowMultipleAudio = audioGroupSchema?.allowMultiple || false;
    const selectedAudioFileIndex = typeof props.activeTimelineAudioGroup?.selectedFileIndex === 'number'
        ? props.activeTimelineAudioGroup.selectedFileIndex
        : null;
    const visibleAudioIndices = audioCustomisationVars
        ? (selectedAudioFileIndex !== null && audioCustomisationVars[selectedAudioFileIndex]
            ? [selectedAudioFileIndex]
            : audioCustomisationVars.map((_, index) => index))
        : [];

    let disruptorGroupDataLinkArray = props.activeTimelineDisruptorGroup ? props.activeTimelineDisruptorGroup?.disruptorData?.['disruptor-select']?.length ? props.activeTimelineDisruptorGroup.disruptorData['disruptor-select'].split('.') : null : null;
    if (disruptorGroupDataLinkArray === null) {
        // Maybe this is a simple toggle?
        disruptorGroupDataLinkArray = props.activeTimelineDisruptorGroup ? props.activeTimelineDisruptorGroup.disruptorData?.['disruptor-enabled']?.length ? props.activeTimelineDisruptorGroup.disruptorData['disruptor-enabled'].split('.') : null : null;
    }
    const disruptorGroupDataLink = disruptorGroupDataLinkArray ? disruptorGroupDataLinkArray.slice(0, -1).join('.') : null;
    const disruptorCustomisationVars = props.activeTimelineDisruptorGroup ? getGroupData(customisationVars, disruptorGroupDataLink, -1) : null;
    const disruptorGroupSchema = getCustomisationFieldData(props.campaignData, props.selectedFeatureNode?.featureData?._id, disruptorGroupDataLink, -1);
    const allowMultipleDisruptors = disruptorGroupSchema?.allowMultiple || false;

    // Are we trying to display an item that is out of bounds?
    let itemOutOfBounds = false;
    if (!isListingMultiples && activeNodeDataIndex !== null && groupsValsArray && (activeNodeDataIndex < 0 || activeNodeDataIndex >= groupsValsArray.length)) {
        itemOutOfBounds = true;
    }

    const AddMultiplesButton = (props) => {
        const handleAdd = (e) => {
            e.stopPropagation();
            if (isListingMultiples) {
                addVal(groupsValsArray, groupSchema.valSchema, activeNode.featureData.feature, forceModuleListUpdate, activeNode);
            } else {
                addVal(
                    audioGroupDataLink ? audioCustomisationVars : disruptorGroupDataLink ? disruptorCustomisationVars : null,
                    audioGroupDataLink ? audioGroupSchema.valSchema : disruptorGroupDataLink ? disruptorGroupSchema.valSchema : null,
                    selectedFeatureNode.featureData.feature,
                    forceModuleListUpdate,
                    selectedFeatureNode
                );
            }
        };

        return (
            <div className="v2-basic-module-holder multiples-header add-cta" onClick={handleAdd}>
                <img src={WORKSPACE_LEFT_LIST_ICONS.add} alt="Add Option" className='plus-icon' onClick={handleAdd} />
                <h5>{props.label}</h5>
            </div>
        );
    }

    const renderDeleteButton = (onDelete) => (
        <div className="delete-icon-button" onClick={onDelete}>
            {IconJsxer.GetIcon(IconJsxer.ICONS.trash, IconJsxer.ICON_STYLES.roundPanelButtonSmall, 'delete-icon')}
        </div>
    );

    return (
        <div className="v2-module-list">
            {listNode?.config?.groupSettings?.csvUploader?.enabled &&
                <div className="v2-basic-module-holder">
                    <div className='v2-module-columns-holder'>
                        <div className="v2-module-one-column v2-full-width">
                            <button className="v2-module-button" onClick={() => slideOutPanelControl.openCsvUploader(listNode?.config?.groupSettings?.csvUploader, () => {}, () => {props.forceUpdateComponent?.(); toast.success('CSV file imported successfully'); triggerSaveFeatureData(listNode?.featureData?._id);}, `Upload/Download ${listNode?.name} CSV File`, listNode)}>
                                Upload/Download CSV File
                            </button>
                        </div>
                    </div>
                </div>
            }
            {groupSchema?.presets?.length > 0 &&
                <PresetsModule
                    {...props}
                    presets={groupSchema.presets}
                    vals={groupsValsArray}
                    defaultVals={groupSchema.vals}
                    forceUpdateComponent={props.forceUpdateComponent}
                    fixSrcUrlsFunc={props.fixSrcUrlsFunc}
                    featureData={props.activeNode.featureData}
                    updateModulesListFunc={forceModuleListUpdate}
                    getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                />
            }
            {audioCustomisationVars && visibleAudioIndices.map((index) => {
                const audioNode = audioCustomisationVars[index];
                return (
                    <Fragment key={index + '_' + forceUpdateInc}>
                        <div className="v2-basic-module-holder multiples-header">
                            <h5>{findDynamicNodeName({...audioNode, config: {children: [{fields: [{dataLink: audioGroupDataLink}]}]}, customisationVars: customisationVars}, index) ?? 'Audio ' + (index + 1)}</h5>
                            {audioCustomisationVars.length > 1 &&
                                renderDeleteButton(() => {removeVal(audioCustomisationVars, index, null, audioNode); props.forceUpdateComponent(); forceModuleListUpdate();})
                            }
                        </div>
                        <AudioModule
                            moduleConfig={props.activeTimelineAudioGroup?.audioData}
                            index={index}
                            visualisationComponentKey={visualisationComponentKey}
                            selectedFeatureNode={props.selectedFeatureNode}
                            customisationVars={customisationVars}
                            campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                            forceUpdate={props.forceUpdate}
                            forceUpdateComponent={props.forceUpdateComponent}
                            openTemporaryAudioUploadPopup={props.openTemporaryAudioUploadPopup}
                            updateModulesListFunc={forceModuleListUpdate}
                            getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                            activateNodeFunction={props.activateNodeFunction}
                        />
                    </Fragment>
                );
            })}
            {disruptorCustomisationVars && disruptorCustomisationVars.map((disruptorNode, index) => {
                return (
                    <Fragment key={index + '_' + forceUpdateInc}>
                        <div className="v2-basic-module-holder multiples-header">
                            <h5>{findDynamicNodeName(disruptorNode, index) ?? 'Disruptor ' + (index + 1)}</h5>
                            {disruptorCustomisationVars.length > 1 &&
                                renderDeleteButton(() => {removeVal(disruptorCustomisationVars, index, null, disruptorNode); props.forceUpdateComponent(); forceModuleListUpdate();})
                            }
                        </div>
                        <DisruptorModule
                            moduleConfig={props.activeTimelineDisruptorGroup?.disruptorData}
                            index={index}
                            visualisationComponentKey={visualisationComponentKey}
                            selectedFeatureNode={props.selectedFeatureNode}
                            customisationVars={customisationVars}
                            campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                            forceUpdate={props.forceUpdate}
                            forceUpdateComponent={props.forceUpdateComponent}
                            openTemporaryImageUploadPopup={props.openTemporaryImageUploadPopup}
                            openTemporaryAudioUploadPopup={props.openTemporaryAudioUploadPopup}
                            openClassicCustomisationSubsetPopup={props.openClassicCustomisationSubsetPopup}
                            getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                            visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                            setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                            activateNodeFunction={props.activateNodeFunction}
                        />
                    </Fragment>
                );
            })}
            {props.activeNode && nodesToList.length > 0 && nodesToList.map((node, index) => {
                const dataIndex = isListingMultiples ? (node.dataIndex ?? index) : (activeNodeDataIndex ?? 0);
                return (
                    <Fragment key={index + '_' + dataIndex + '_' + forceUpdateInc}>
                        {isListingMultiples &&
                            <div className="v2-basic-module-holder multiples-header">
                                <h5>{findDynamicNodeName(node, dataIndex) ?? props.activeNode.name + ' ' + (dataIndex + 1)}</h5>
                                {groupsValsArray.length > 1 &&
                                    renderDeleteButton(() => {removeVal(groupsValsArray, dataIndex, null, node); props.forceUpdateComponent(); forceModuleListUpdate();})
                                }
                            </div>
                        }
                        {itemOutOfBounds &&
                            <div className="v2-basic-module-holder">
                                <h5>Deleted</h5>
                            </div>
                        }
                        {!itemOutOfBounds && groupChildren.map((module, index) => {
                            if (module?.moduleSettings?.showInModuleList === false) {
                                // Skip modules that are not meant to be shown in the module list
                                return null;
                            }
                            if (module.type === 'module' && module.module === 'position') {
                                return (
                                    <PositionModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'dimensions') {
                                return (
                                    <DimensionsModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'appearance') {
                                return (
                                    <AppearanceModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'border') {
                                return (
                                    <BorderModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'fill') {
                                return (
                                    <FillModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        openTemporaryImageUploadPopup={props.openTemporaryImageUploadPopup}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'typography') {
                                return (
                                    <TypographyModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        openFontSelector={props.openFontSelector}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'padding') {
                                return (
                                    <PaddingModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'copy') {
                                return (
                                    <CopyModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        customisationVars={customisationVars}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'animation') {
                                return (
                                    <AnimationModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        customisationVars={customisationVars}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'shadow') {
                                return (
                                    <ShadowModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        customisationVars={customisationVars}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        openFontSelector={props.openFontSelector}
                                        openTemporaryImageUploadPopup={props.openTemporaryImageUploadPopup}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'custom') {
                                return (
                                    <CustomModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        customisationVars={customisationVars}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        forceModuleListUpdate={forceModuleListUpdate}
                                        openFontSelector={props.openFontSelector}
                                        openTemporaryImageUploadPopup={props.openTemporaryImageUploadPopup}
                                        openClassicCustomisationSubsetPopup={props.openClassicCustomisationSubsetPopup}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'audio') {
                                return (
                                    <AudioModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        customisationVars={customisationVars}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        openFontSelector={props.openFontSelector}
                                        openTemporaryImageUploadPopup={props.openTemporaryImageUploadPopup}
                                        openTemporaryAudioUploadPopup={props.openTemporaryAudioUploadPopup}
                                        updateModulesListFunc={forceModuleListUpdate}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            if (module.type === 'module' && module.module === 'disruptor') {
                                return (
                                    <DisruptorModule
                                        key={index + '_' + forceUpdateInc}
                                        moduleConfig={module}
                                        index={dataIndex}
                                        groupConfig={groupConfig}
                                        visualisationComponentKey={visualisationComponentKey ?? findCustomRendererKey(node)}
                                        customisationVars={customisationVars}
                                        selectedFeatureNode={props.selectedFeatureNode}
                                        campaignData={props.campaignData ?? WORKING_ON_NODE_DATA.campaignData ?? campaignContext?.campaignData}
                                        forceUpdate={props.forceUpdate}
                                        forceUpdateComponent={props.forceUpdateComponent}
                                        openFontSelector={props.openFontSelector}
                                        openTemporaryImageUploadPopup={props.openTemporaryImageUploadPopup}
                                        openTemporaryAudioUploadPopup={props.openTemporaryAudioUploadPopup}
                                        openClassicCustomisationSubsetPopup={props.openClassicCustomisationSubsetPopup}
                                        getCustomisationVarsForFeature={props.getCustomisationVarsForFeature}
                                        visibleTriggeredDisruptorId={props.visibleTriggeredDisruptorId}
                                        setVisibleTriggeredDisruptorId={props.setVisibleTriggeredDisruptorId}
                                        activateNodeFunction={props.activateNodeFunction}
                                    />
                                );
                            }
                            // Add other module types here as needed
                            return null;
                        })}
                    </Fragment>
                )
            })}
            {(allowMultipleDisruptors || allowMultipleAudio || isListingMultiples) &&
                <AddMultiplesButton
                    label={resolveAddMultiplesLabel({
                        audioCustomisationVars,
                        disruptorCustomisationVars,
                        activeNodeName: props.activeNode?.name,
                    })}
                    customisationVars={customisationVars}
                    groupConfig={groupConfig}
                    activeNode={props.activeNode}
                    forceUpdateComponent={props.forceUpdateComponent}
                />
            }
            {!isListingMultiples && !allowMultipleDisruptors && !allowMultipleAudio && typeof activeNodeDataIndex === 'number' && groupSchema?.allowMultiple && props?.activeNode?.parent &&
                <div className="v2-basic-module-holder multiples-header">
                    <h5 style={{cursor: 'pointer'}} onClick={() => props.activateNodeFunction(props.activeNode.parent)}>List All {props.activeNode.parent?.name}</h5>
                </div>
            }
        </div>
    );
}

export default ModuleList;
